import type { AdmissionRecheckItemResult, BatchJob } from "../../../../../lib/corpusApi";
import { narrowByOutcome, type BatchJobItemRow } from "../../panel/batch_job_item_rows";
import {
  useBatchJobItemList,
  type UseBatchJobItemListResult,
} from "../../panel/useBatchJobItemList";
import {
  useBatchJobItemReview,
  type UseBatchJobItemReviewResult,
} from "../../panel/useBatchJobItemReview";
import {
  useBatchJobItemSelection,
  type BatchJobItemSelectionView,
} from "../../panel/useBatchJobItemSelection";
import { useBatchJobTabs, type UseBatchJobTabsResult } from "../../panel/useBatchJobTabs";
import { useBulkItemReview, type UseBulkItemReviewResult } from "../../panel/useBulkItemReview";
import {
  useDocumentDetailTarget,
  type UseDocumentDetailTargetResult,
} from "../../panel/useDocumentDetailTarget";
import {
  useRejectionCodeFilter,
  type UseRejectionCodeFilterResult,
} from "../../panel/useRejectionCodeFilter";
import {
  ADMISSION_RECHECK_TABS,
  type AdmissionRecheckTabConfig,
  type AdmissionRecheckTabKey,
} from "./admission_recheck_tabs";

/** One list row: the raw item plus its admission_recheck result. A failed item has none. */
export type AdmissionRecheckRow = BatchJobItemRow<AdmissionRecheckItemResult>;

/** What the panel can do about a document the gate would now reject: keep it in the corpus
 *  (dismiss) or revoke it (apply), one at a time or many. */
export interface AdmissionRecheckReview extends UseBatchJobItemReviewResult<AdmissionRecheckItemResult> {
  bulkApply: UseBulkItemReviewResult;
}

export interface UseAdmissionRecheckPanelResult {
  tabs: UseBatchJobTabsResult<AdmissionRecheckTabKey, AdmissionRecheckTabConfig>;
  filters: UseRejectionCodeFilterResult;
  list: UseBatchJobItemListResult<AdmissionRecheckItemResult>;
  selection: BatchJobItemSelectionView<AdmissionRecheckItemResult>;
  review: AdmissionRecheckReview;
  detail: UseDocumentDetailTargetResult<AdmissionRecheckItemResult>;
}

const narrowAdmissionRecheckResult = narrowByOutcome<AdmissionRecheckItemResult>(
  new Set(["admitted", "rejected", "escalated", "skipped"])
);

function toDocumentTarget(row: AdmissionRecheckRow) {
  return { id: row.item.subject };
}

/** What a Revoke would revoke the document for, for its confirmation. */
function revokeMessage(row: AdmissionRecheckRow | null): string {
  const rejection = row?.result?.rejection;
  return rejection
    ? `${rejection.code}: ${rejection.message}`
    : "Revoke this document from the corpus?";
}

/** Drives the admission_recheck batch job panel: a tabbed set of paginated review queues, each
 *  with per-item and bulk revoke/keep actions. */
export function useAdmissionRecheckPanel(job: BatchJob): UseAdmissionRecheckPanelResult {
  const tabs = useBatchJobTabs(ADMISSION_RECHECK_TABS, "candidates");
  const filters = useRejectionCodeFilter(tabs.activeTab);
  const resetKey = `${tabs.activeTab}:${filters.rejectionCode ?? ""}`;

  const list = useBatchJobItemList({
    job,
    filters: {
      ...tabs.tab.apiFilters,
      ...(filters.rejectionCode
        ? { admissionRecheck: { rejectionCode: filters.rejectionCode } }
        : {}),
    },
    resetKey,
    narrow: narrowAdmissionRecheckResult,
  });
  const selection = useBatchJobItemSelection({
    rows: list.rows,
    enabled: tabs.tab.reviewable,
    page: list.pagination.page,
    resetKey,
  });

  const single = useBatchJobItemReview({
    job,
    refresh: list.refresh,
    applyCopy: {
      title: "Revoke document",
      message: revokeMessage,
      confirmLabel: "Revoke",
      confirmColor: "error",
    },
  });
  const bulkApply = useBulkItemReview({
    jobId: job.id,
    decision: "apply",
    selection: selection.state,
    refresh: list.refresh,
    confirm: {
      title: "Revoke selected documents",
      message: `Revoke ${selection.count} document(s) from the corpus? This cannot be undone.`,
      confirmLabel: "Revoke selected",
      confirmColor: "error",
    },
    failureVerb: "revoke",
  });

  return {
    tabs,
    filters,
    list,
    selection: { table: selection.table, count: selection.count },
    review: { ...single, bulkApply },
    detail: useDocumentDetailTarget(toDocumentTarget),
  };
}
